import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Button,
  styled,
  useTheme,
  IconButton,
} from '@mui/material';
import { languages, useLanguage } from '../data/languages';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import SentimentVeryDissatisfiedIcon from '@mui/icons-material/SentimentVeryDissatisfied';

// Style the continue button to match QuestionSplit
const ContinueButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(4),
  borderRadius: theme.spacing(3),
  padding: theme.spacing(1.5, 5),
  fontSize: '1.15rem',
  fontFamily: "'Space Grotesk', sans-serif",
  textTransform: 'none',
  backgroundColor: theme.palette.primary.main,
  color: '#fff',
  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
  },
  transition: 'all 0.3s ease',
}));

// Style the message container to match QuestionSplit's reader message
const ConfidenceContainer = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  padding: theme.spacing(4, 4),
  borderRadius: theme.spacing(2),
  width: '100%',
  maxWidth: '600px',
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  alignSelf: 'center',
  margin: theme.spacing(2, 0),
}));

// Enhanced component for the sentiment icons row
const SentimentIconsRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(2),
  position: 'relative',
}));

// Styled IconButton for sentiment selection
const SentimentIconButton = styled(IconButton)(({ theme, selected }) => ({
  padding: theme.spacing(2),
  transition: 'all 0.25s ease',
  transform: selected ? 'scale(1.3)' : 'scale(1)',
  opacity: selected ? 1 : 0.5,
  backgroundColor: selected ? 'rgba(0, 0, 0, 0.05)' : 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.03)',
    opacity: 0.9,
    transform: selected ? 'scale(1.3)' : 'scale(1.15)',
  }
}));

const ConfidenceRating = ({ 
  value, 
  onChange, 
  onSubmit, 
  buttonText, // 移除默认值，使用配置文件
  showButton = true
}) => {
  const theme = useTheme();
  const currentLang = useLanguage(); // 响应式语言获取
  const config = languages[currentLang] || languages['en']; // 获取语言配置，带fallback
  
  // Define the sentiment options for the inline confidence rating
  const sentimentOptions = [
    { value: 1, icon: SentimentVeryDissatisfiedIcon, color: '#d32f2f', label: config.confidenceRating.sentimentLabels[1] },
    { value: 2, icon: SentimentDissatisfiedIcon, color: '#ef6c00', label: config.confidenceRating.sentimentLabels[2] },
    { value: 3, icon: SentimentNeutralIcon, color: '#ffb300', label: config.confidenceRating.sentimentLabels[3] },
    { value: 4, icon: SentimentSatisfiedAltIcon, color: '#689f38', label: config.confidenceRating.sentimentLabels[4] },
    { value: 5, icon: SentimentVerySatisfiedIcon, color: '#2e7d32', label: config.confidenceRating.sentimentLabels[5] }
  ];
  
  // Handle icon click
  const handleIconClick = (newValue) => {
    onChange(null, newValue);
  };
  
  // Get the current sentiment option
  const getCurrentSentiment = () => {
    return sentimentOptions.find(option => option.value === value) || sentimentOptions[2]; // Default to neutral
  };
  
  return (
    <ConfidenceContainer>
      {/* <Typography variant="h6" sx={{ 
        fontFamily: "'Space Grotesk', sans-serif",
        fontSize: '1.3rem',
        fontWeight: 500,
        mb: 2,
        textAlign: 'center'
      }}>
        {promptText}
      </Typography>
       */}
      {/* Display all sentiment icons in a row */}
      <SentimentIconsRow>
        {sentimentOptions.map((option) => {
          const IconComponent = option.icon;
          const isSelected = value === option.value;
          
          return (
            <Box key={option.value} sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              gap: 1
            }}>
              <SentimentIconButton 
                selected={isSelected}
                onClick={() => handleIconClick(option.value)}
                disableRipple
              >
                <IconComponent 
                  sx={{ 
                    fontSize: 52,
                    color: option.color,
                    transition: 'all 0.2s ease',
                  }} 
                />
              </SentimentIconButton>
            </Box>
          );
        })}
      </SentimentIconsRow>
      
      {/* Display sentiment text */}
      <Typography sx={{ 
        fontFamily: "'Space Grotesk', sans-serif",
        fontSize: '1.2rem',
        color: getCurrentSentiment().color,
        fontWeight: 600,
        mb: 2,
        textAlign: 'center',
        minHeight: '32px'
      }}>
        {getCurrentSentiment().label}
      </Typography>
      
      {showButton && (
        <ContinueButton
          variant="contained"
          style={{ backgroundColor: getCurrentSentiment().color }}
          onClick={onSubmit}
        >
          {buttonText || config.confidenceRating.continue}
        </ContinueButton>
      )}
    </ConfidenceContainer>
  );
};

export default ConfidenceRating; 